import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import s from './FidelidadeConfig.module.css';

const CONFIG_PADRAO = { ativo: false, public_key: '', mp_conta_id: '', point_ativo: false };

export default function MercadoPagoConfig() {
  const { usuario } = useAuth();
  const orgId = usuario?.organizacao_id;

  const [carregando, setCarregando] = useState(true);
  const [config, setConfig] = useState(CONFIG_PADRAO);
  const [accessToken, setAccessToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [tokenConfigurado, setTokenConfigurado] = useState(false);
  const [webhookConfigurado, setWebhookConfigurado] = useState(false);
  const [contas, setContas] = useState([]);
  const [todasContas, setTodasContas] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  const setC = (campo, valor) => setConfig(prev => ({ ...prev, [campo]: valor }));

  useEffect(() => {
    if (!orgId) return;
    Promise.all([
      api.get(`/organizacoes/${orgId}/mercadopago`),
      api.get('/financeiro/contas'),
    ]).then(([{ data }, { data: contasData }]) => {
      const { access_token_configurado, webhook_secret_configurado, ...cfg } = data;
      setConfig({ ...CONFIG_PADRAO, ...cfg });
      setTokenConfigurado(!!access_token_configurado);
      setWebhookConfigurado(!!webhook_secret_configurado);
      setContas((contasData || []).filter(c => c.ativo));
      setTodasContas(contasData || []);
    }).catch(() => setErro('Erro ao carregar configurações do Mercado Pago.'))
      .finally(() => setCarregando(false));
  }, [orgId]);

  const salvar = async () => {
    setErro(''); setSucesso(''); setSalvando(true);
    try {
      const { data } = await api.put(`/organizacoes/${orgId}/mercadopago`, {
        ...config,
        access_token: accessToken || undefined,
        webhook_secret: webhookSecret || undefined,
      });
      const { access_token_configurado, webhook_secret_configurado, ...cfg } = data;
      setConfig({ ...CONFIG_PADRAO, ...cfg });
      setTokenConfigurado(!!access_token_configurado);
      setWebhookConfigurado(!!webhook_secret_configurado);
      setAccessToken('');
      setWebhookSecret('');
      setSucesso('Configurações salvas com sucesso!');
      setTimeout(() => setSucesso(''), 3000);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar configurações.');
    } finally {
      setSalvando(false);
    }
  };

  const urlWebhook = `${window.location.origin}/api/webhooks/mercadopago/${orgId}`;

  if (carregando) return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>💳</span> Mercado Pago</div>
      <div className={s.conteudo}>Carregando...</div>
    </div>
  );

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>💳</span> Mercado Pago</div>

      <div className={s.conteudo}>
        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Pagamento Online no Catálogo</legend>
          <label className={s.checkRow}>
            <input type="checkbox" checked={!!config.ativo} onChange={e => setC('ativo', e.target.checked)} />
            Aceitar pagamento online na vitrine (PIX, débito, crédito)
          </label>
          <p className={s.ajudaCampo}>
            O cliente escolhe a forma de pagamento direto na vitrine; depois de confirmado, um
            pedido já aprovado aparece na Pré-venda (F2) do PDV — sem precisar cobrar de novo.
          </p>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Credenciais</legend>

          <div className={s.campo}>
            <span className={s.campoLabel}>Public Key:</span>
            <input className={s.inputErp} value={config.public_key}
              onChange={e => setC('public_key', e.target.value)}
              placeholder="APP_USR-xxxxxxxx-xxxxxx" />
          </div>

          <div className={s.campo}>
            <span className={s.campoLabel}>Access Token:</span>
            <input className={s.inputErp} type="password" value={accessToken}
              onChange={e => setAccessToken(e.target.value)}
              placeholder={tokenConfigurado ? '•••••••• (já configurado — deixe em branco pra manter)' : 'APP_USR-xxxxxxxx...'} />
          </div>

          <div className={s.campo}>
            <span className={s.campoLabel}>Webhook Secret:</span>
            <input className={s.inputErp} type="password" value={webhookSecret}
              onChange={e => setWebhookSecret(e.target.value)}
              placeholder={webhookConfigurado ? '•••••••• (já configurado — deixe em branco pra manter)' : 'chave secreta do webhook'} />
          </div>
          <p className={s.ajudaCampo}>
            As três informações ficam no seu painel Mercado Pago em <strong>Seu negócio → Configurações
            → Credenciais</strong> (Public Key + Access Token) e em <strong>Webhooks</strong> (Webhook Secret,
            gerado ao cadastrar a URL abaixo). Access Token e Webhook Secret ficam criptografados
            no banco e nunca são exibidos de volta depois de salvos.
          </p>

          <div className={s.campo} style={{ alignItems: 'flex-start' }}>
            <span className={s.campoLabel}>URL do Webhook:</span>
            <code style={{
              flex: 1, minWidth: 160, fontSize: '0.8rem', padding: '6px 10px',
              background: 'var(--content-bg)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', wordBreak: 'break-all', userSelect: 'all',
            }}>{urlWebhook}</code>
          </div>
          <p className={s.ajudaCampo}>
            Cole essa URL no painel do Mercado Pago, em <strong>Webhooks → Configurar notificações</strong>,
            marcando o evento <strong>Pagamentos</strong>. É assim que o sistema fica sabendo quando um
            PIX foi pago de verdade (o cliente pode demorar pra pagar depois de gerar o QR Code).
            Usando a maquininha Point, marque também o evento <strong>Order (Mercado Pago)</strong>.
          </p>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Conta de Destino (opcional)</legend>
          <div className={s.campo}>
            <span className={s.campoLabel}>Lançar recebimentos em:</span>
            <select className={s.inputErp} value={config.mp_conta_id || ''}
              onChange={e => setC('mp_conta_id', e.target.value || null)}>
              <option value="">— não lançar em conta nenhuma (só o histórico) —</option>
              {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <p className={s.ajudaCampo}>
            Se você tiver uma conta financeira cadastrada representando o saldo Mercado Pago,
            escolha aqui — os recebimentos do checkout entram nela automaticamente, igual já
            acontece com PIX e Boleto.
          </p>
        </fieldset>

        <fieldset className={s.fieldset}>
          <legend className={s.legend}>Maquininha Point no PDV</legend>
          <label className={s.checkRow}>
            <input type="checkbox" checked={!!config.point_ativo} onChange={e => setC('point_ativo', e.target.checked)} />
            Cobrar cartão direto na maquininha Mercado Pago pelo PDV
          </label>
          <p className={s.ajudaCampo}>
            No F9, com a forma em Cartão, aparece o botão <strong>💳 Maquininha (M)</strong>: o valor vai
            sozinho para a maquininha e, aprovado, entra na venda com bandeira, parcelas e código de
            autorização (a NFC-e sai como pagamento integrado). Usa o mesmo Access Token acima.
            Clique em <strong>Salvar</strong> depois de marcar.
          </p>
          {config.point_ativo && tokenConfigurado && (
            <PointMaquininhas caixas={todasContas.filter(c => c.tipo === 'caixa' && c.ativo)} />
          )}
          {config.point_ativo && !tokenConfigurado && (
            <p className={s.erroMsg}>Configure e salve o Access Token antes de cadastrar as maquininhas.</p>
          )}
        </fieldset>

        {erro    && <div className={s.erroMsg}>{erro}</div>}
        {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}
      </div>

      <div className={s.rodape}>
        <button className={s.btnErpPrimario} onClick={salvar} disabled={salvando} style={{ marginLeft: 'auto' }}>
          {salvando ? 'Salvando...' : '✔ Salvar'}
        </button>
      </div>
    </div>
  );
}

const STATUS_COB = {
  pendente: 'Aguardando', aprovado: 'Aprovado', recusado: 'Recusado', cancelado: 'Cancelado',
  expirado: 'Expirado', estornado: 'Estornado',
};

// Cadastro das maquininhas (vinculadas a cada caixa) + últimas cobranças,
// com estorno das que ficaram aprovadas sem venda (PDV fechado no meio etc.).
function PointMaquininhas({ caixas }) {
  const [terminais, setTerminais] = useState([]);
  const [daConta, setDaConta] = useState(null);
  const [cobrancas, setCobrancas] = useState([]);
  const [msg, setMsg] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const carregar = () => Promise.all([
    api.get('/mp-point/terminais').then(({ data }) => setTerminais(data)),
    api.get('/mp-point/cobrancas').then(({ data }) => setCobrancas(data)),
  ]).catch(() => setMsg('Erro ao carregar maquininhas.'));
  useEffect(() => { carregar(); }, []);

  const executar = async (fn, ok) => {
    setMsg(''); setOcupado(true);
    try { await fn(); if (ok) setMsg(ok); await carregar(); }
    catch (err) { setMsg(err.response?.data?.erro || 'Erro na operação.'); }
    finally { setOcupado(false); }
  };

  const buscarDaConta = () => executar(async () => {
    const { data } = await api.get('/mp-point/terminais/mercadopago');
    setDaConta(data);
  });
  const salvar = t => executar(() => api.post('/mp-point/terminais', t), 'Maquininha salva.');
  const ativarPdv = t => executar(() => api.post(`/mp-point/terminais/${t.id}/ativar-pdv`),
    'Modo PDV ativado. Reinicie a maquininha para valer.');
  const remover = t => window.confirm(`Remover a maquininha ${t.nome || t.terminal_id}?`) &&
    executar(() => api.delete(`/mp-point/terminais/${t.id}`));
  const estornar = c => window.confirm(`Estornar R$ ${Number(c.valor).toFixed(2).replace('.', ',')} no cartão do cliente?`) &&
    executar(() => api.post(`/mp-point/cobrancas/${c.id}/estornar`), 'Estorno solicitado.');

  const jaCadastrada = id => terminais.some(t => t.terminal_id === id);
  const cel = { padding: '4px 6px', borderBottom: '1px solid var(--border)', fontSize: '0.8rem', textAlign: 'left' };

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontWeight: 600, fontSize: '0.85rem', margin: '6px 0' }}>Maquininhas da loja</div>
      {terminais.length === 0 && <p className={s.ajudaCampo}>Nenhuma cadastrada — clique em "Buscar na conta Mercado Pago".</p>}
      {terminais.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={cel}>Nome</th><th style={cel}>Caixa</th><th style={cel}>Ativa</th><th style={cel}></th></tr></thead>
          <tbody>
            {terminais.map(t => (
              <tr key={t.id}>
                <td style={cel}>
                  <input className={s.inputCurto} defaultValue={t.nome || ''} maxLength={60} style={{ width: 130 }}
                    onBlur={e => e.target.value !== (t.nome || '') && salvar({ ...t, nome: e.target.value })} />
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{t.terminal_id}</div>
                </td>
                <td style={cel}>
                  <select className={s.inputCurto} value={t.conta_caixa_id || ''} disabled={ocupado}
                    onChange={e => salvar({ ...t, conta_caixa_id: e.target.value ? parseInt(e.target.value, 10) : null })}>
                    <option value="">— qualquer caixa —</option>
                    {caixas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </td>
                <td style={cel}>
                  <input type="checkbox" checked={!!t.ativo} disabled={ocupado} onChange={e => salvar({ ...t, ativo: e.target.checked })} />
                </td>
                <td style={{ ...cel, whiteSpace: 'nowrap' }}>
                  <button className={s.btnErpPrimario} disabled={ocupado} onClick={() => ativarPdv(t)}
                    title="Coloca a maquininha em modo PDV (necessário para receber cobranças do sistema)">Modo PDV</button>{' '}
                  <button disabled={ocupado} onClick={() => remover(t)} title="Remover" style={{ cursor: 'pointer' }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className={s.ajudaCampo}>
        Com vários caixas, vincule cada maquininha ao seu caixa: o PDV usa a do caixa aberto pelo operador.
        Com uma só, ela atende qualquer caixa.
      </p>

      <button className={s.btnErpPrimario} disabled={ocupado} onClick={buscarDaConta} style={{ marginTop: 6 }}>
        🔎 Buscar na conta Mercado Pago
      </button>
      {daConta && (
        <div style={{ marginTop: 8 }}>
          {daConta.length === 0 && (
            <p className={s.ajudaCampo}>
              Nenhuma maquininha encontrada. Vincule a maquininha à sua conta e a um caixa (loja/PDV) pelo
              app do Mercado Pago e busque de novo.
            </p>
          )}
          {daConta.map(t => (
            <div key={t.id} className={s.campo} style={{ fontSize: '0.8rem' }}>
              <code>{t.id}</code>
              <span style={{ color: t.operating_mode === 'PDV' ? 'green' : 'var(--text-muted)' }}>
                modo {t.operating_mode || '?'}
              </span>
              {jaCadastrada(t.id)
                ? <span style={{ color: 'var(--text-muted)' }}>já cadastrada</span>
                : <button className={s.btnErpPrimario} disabled={ocupado}
                    onClick={() => salvar({ terminal_id: t.id, nome: `Maquininha ${terminais.length + 1}` })}>+ Adicionar</button>}
            </div>
          ))}
        </div>
      )}

      {msg && <div className={s.sucessoMsg} style={{ marginTop: 8 }}>{msg}</div>}

      {cobrancas.length > 0 && (
        <>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', margin: '14px 0 6px' }}>Últimas cobranças na maquininha</div>
          <div style={{ maxHeight: 260, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={cel}>Data</th><th style={cel}>Valor</th><th style={cel}>Tipo</th><th style={cel}>Situação</th><th style={cel}>Venda</th><th style={cel}></th></tr></thead>
              <tbody>
                {cobrancas.map(c => {
                  const podeEstornar = c.status === 'aprovado' && (!c.venda_id || c.venda_status === 'cancelada');
                  return (
                    <tr key={c.id}>
                      <td style={cel}>{new Date(c.criado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td style={cel}>R$ {Number(c.valor).toFixed(2).replace('.', ',')}</td>
                      <td style={cel}>{c.tipo === 'debito' ? 'Débito' : `Crédito ${c.parcelas}x`}{c.bandeira ? ` · ${c.bandeira}` : ''}</td>
                      <td style={cel}>{STATUS_COB[c.status] || c.status}</td>
                      <td style={cel}>
                        {c.venda_id ? `#${c.venda_id}${c.venda_status === 'cancelada' ? ' (cancelada)' : ''}`
                          : c.status === 'aprovado' ? <strong style={{ color: '#b45309' }}>sem venda</strong> : '—'}
                      </td>
                      <td style={cel}>
                        {podeEstornar && <button disabled={ocupado} onClick={() => estornar(c)} style={{ cursor: 'pointer' }}>Estornar</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className={s.ajudaCampo}>
            "Sem venda" = cobrado na maquininha mas a venda não foi finalizada no PDV. Estorne aqui ou
            refaça a venda. Venda cancelada no sistema também libera o estorno do cartão.
          </p>
        </>
      )}
    </div>
  );
}
